/**
 * Which year a screen opens on (walk fixes 2, item 10). Two answers, and they are not the same:
 *
 * - the **planning year** is the festival being planned — this year's festival until its Sunday
 *   has passed, and the next one from the Monday after. In closeout, the January asks, the
 *   sponsor levels, the gifts and the shifts all belong to next year, not to the one being
 *   closed. A screen that opens on the year closing out asks a director to change the year
 *   before every single thing they do.
 * - the **door year** is the year the public doors write to, which Year settings sets on each
 *   door. It is usually the planning year and does not have to be: a director can open next
 *   year's family declaration in the middle of this year's festival.
 *
 * The year in the address always wins over both: a link somebody was sent opens on its own year.
 */
import { useState } from 'react';
import { FestivalClock } from '@cider/domain/engines';
import type { usePhase } from './phase.js';

export type Phase = ReturnType<typeof usePhase>;

/**
 * The calendar year in Detroit, today — never the machine's UTC year, which after 7 PM on New
 * Year's Eve is already next year's (ADR 0006). Only a fallback before the phase has answered.
 */
export function detroitYear(): number {
  return FestivalClock.wallClock(new Date()).year;
}

/** The festival being planned: this year's until its Sunday has passed, then the next one. */
export function planningYear(phase: Phase): number {
  if (phase.festivalYear)
    return phase.id === 'closeout' || phase.id === 'onboarding' || phase.id === null
      ? phase.festivalYear + 1
      : phase.festivalYear;
  return phase.doorYear ?? detroitYear() + 1;
}

/** The year the doors write to: the declaration door's, else the year being planned. */
export function doorYear(phase: Phase): number {
  return phase.doorYear ?? planningYear(phase);
}

/** The year named in the address, when one is. */
export function yearInAddress(): number | null {
  const y = new URLSearchParams(window.location.search).get('year') ?? '';
  return /^\d{4}$/.test(y) ? Number(y) : null;
}

/** A year the screen can change, starting from one of the two answers above. */
export function useYearFrom(
  from: (phase: Phase) => number,
  phase: Phase,
): [number, (y: number) => void] {
  const [picked, setPicked] = useState<number | null>(yearInAddress());
  return [picked ?? from(phase), setPicked];
}
